import React, { createContext, useEffect, useRef, useState } from "react";
import {
  Challenge,
  ChallengeUpdateMessage,
  PowContextType,
  PowWrapperProps,
  Prefix,
} from "../models/Types";

// We cannot define the initial value here, so we put undefined and tell TS to relax.
// The actual, well-typed value will be set inside PowWrapper.
// @ts-ignore
export const PowContext = createContext<PowContextType>(undefined);

export function PowWrapper({ children }: PowWrapperProps) {
  const [challenges, setChallenges] = useState<Array<Challenge>>([]);
  const [nLeadingZerosRequired, setNLeadingZerosRequired] = useState<number>(0);
  const [answers, setAnswers] = useState<Array<Prefix>>([]);
  const [answerTimes, setAnswerTimes] = useState<Array<DOMHighResTimeStamp>>(
    [],
  );
  const [slowWorker, setSlowWorker] = useState<boolean>(false);
  const powStartTimeMs = useRef<DOMHighResTimeStamp>(performance.now());
  const powContext = {
    answers,
    setAnswers,
    answerTimes,
    challenges,
    setChallenges,
    nLeadingZerosRequired,
    setNLeadingZerosRequired,
    resetPow,
    slowWorker,
  };

  function resetPow() {
    setAnswers([]);
    setAnswerTimes([]);
    setChallenges([]);
    setNLeadingZerosRequired(0);
    powStartTimeMs.current = performance.now();
    setSlowWorker(false);
  }

  function solveChallenges(): () => void {
    setAnswers([]);

    if (challenges.length === 0) {
      // do not start the worker
      const cleanupNoop = () => {};
      return cleanupNoop;
    }

    const worker = new Worker("./worker.min.js", { type: "module" });
    worker.postMessage({
      challenges,
      nLeadingZerosRequired,
    });

    powStartTimeMs.current = performance.now();
    worker.onmessage = (e) => {
      if (e.data.type == "CHALLENGE_UPDATE") {
        const now = performance.now();
        const elapsedMs = now - powStartTimeMs.current;
        powStartTimeMs.current = performance.now();
        const message = e.data as ChallengeUpdateMessage;
        storeAnswer(message.answer, elapsedMs);
      }
    };

    const cleanupKillWorker = () => {
      worker.terminate();
    };
    return cleanupKillWorker;
  }

  function storeAnswer(answer: Prefix, elapsedMs: number) {
    setAnswers((answers) => [...answers, answer]);
    setAnswerTimes((answerTimes) => [...answerTimes, elapsedMs]);
  }

  function elapsedTime() {
    return answerTimes.reduce((acc, x) => acc + x, 0);
  }

  function estimateOverallTime() {
    const S = elapsedTime();
    const p = answerTimes.length;
    const r = challenges.length - p;

    // The formula below was derived under a bayesian framework: it is the Maximum-likelihood estimate (MLE)
    // of the remaining time, according to a sum of i.i.d individual exponential distributions, and based
    // on the prior observations of time spent on individual challenges so far.
    const estRem = (S * (r - 1)) / (p + 2);

    const elapsed = S;
    return elapsed + estRem;
  }

  useEffect(solveChallenges, [challenges]);

  useEffect(() => {
    const elapsedMs = elapsedTime();
    const estimatedOverallTimeMs = estimateOverallTime();
    if (elapsedMs > 10000 && estimatedOverallTimeMs > 20000) {
      setSlowWorker(true);
    }
  }, [answerTimes]);

  return (
    <PowContext.Provider value={powContext}>{children}</PowContext.Provider>
  );
}
